/**
 * Custom hook for managing invoices with API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invoicesAPI, customersAPI } from '@/lib/api';
import { useToast } from '@/hooks/useToast';

const API_BASE_URL = 'https://sourcemygoods.com/api/public';

// Helper function to send emails
async function sendEmail(endpoint: string, data: any) {
  try {
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      console.error(`Failed to send ${endpoint}:`, await response.text());
    }
  } catch (error) {
    console.error(`Error sending ${endpoint}:`, error);
  }
}

export function useInvoices() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data, isLoading, error } = useQuery({
    queryKey: ['invoices'],
    queryFn: async () => {
      const response = await invoicesAPI.getAll();
      return response.data || [];
    },
  });

  const createMutation = useMutation({
    mutationFn: invoicesAPI.create,
    onSuccess: async (response: any, variables: any) => {
      // Refetch queries to ensure UI updates with correct data
      await queryClient.refetchQueries({ queryKey: ['invoices'] });
      toast({ title: 'Success', description: 'Invoice created successfully' });

      // DON'T send email when invoice is created as Draft
      // Email will be sent when status changes to "Sent"
      // No email notification on creation (invoices start as Draft)
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: invoicesAPI.update,
    onSuccess: async (response: any, variables: any) => {
      // Refetch queries to ensure UI updates with correct data
      await queryClient.refetchQueries({ queryKey: ['invoices'] });
      toast({ title: 'Success', description: 'Invoice updated successfully' });

      // Send email if status changed (but NOT for Draft status)
      if (variables.status && variables.status !== 'Draft') {
        try {
          // Get the invoice to find customer code and details
          const invoicesResponse = await invoicesAPI.getAll();
          const invoices = invoicesResponse.data || [];
          const invoice = invoices.find((inv: any) => inv.invoice_number === variables.invoice_number);

          if (invoice) {
            // Fetch customer email
            const customersResponse = await customersAPI.getAll();
            const customers = customersResponse.data || [];
            const customer = customers.find((c: any) => c.code === invoice.customer_code);

            if (customer && customer.email) {
              // If status is changing to "Sent", send the "Invoice Created" email (first time customer sees it)
              if (variables.status === 'Sent') {
                // Build shipment details from the shipments array
                const rate = parseFloat(invoice.rate_per_kg) || 0;
                const shipmentsForEmail = (invoice.shipments || []).map((s: any) => ({
                  shipmentNumber: s.shipmentNumber || s.shipment_number,
                  description: s.description || 'Shipment',
                  weight: parseFloat(s.weight) || 0,
                  packages: parseInt(s.packages) || 0,
                  rate: rate,
                  amount: (parseFloat(s.weight) || 0) * rate
                }));

                // Send "New Invoice" email (this is the first time customer sees it)
                await sendEmail('send-invoice-created-email.php', {
                  to: customer.email,
                  customerName: customer.name || 'Valued Customer',
                  invoiceData: {
                    invoiceNumber: variables.invoice_number,
                    subtotal: invoice.subtotal || 0,
                    tax: invoice.tax || 0,
                    discount: invoice.discount || 0,
                    total: invoice.total || 0,
                    dueDate: invoice.due_date || '',
                    shipments: shipmentsForEmail,
                    lineItems: invoice.line_items || [],
                  },
                });
              } else {
                // For other status changes (Paid, Overdue, Partial), send status update email with shipments
                const rate = parseFloat(invoice.rate_per_kg) || 0;
                const shipmentsForEmail = (invoice.shipments || []).map((s: any) => {
                  const freightMethod = s.freightMethod || s.freight_method || 'Air';
                  const weight = parseFloat(s.weight) || 0;
                  const volume = parseFloat(s.volume) || 0;
                  const amount = freightMethod === 'Air' ? (weight * rate) : (volume * rate);
                  
                  return {
                    shipmentNumber: s.shipmentNumber || s.shipment_number,
                    description: s.description || 'Shipment',
                    weight: weight,
                    volume: volume,
                    packages: parseInt(s.packages) || 0,
                    freightMethod: freightMethod,
                    rate: rate,
                    amount: amount
                  };
                });
                
                await sendEmail('send-invoice-status-email.php', {
                  to: customer.email,
                  customerName: customer.name || 'Valued Customer',
                  invoiceNumber: variables.invoice_number,
                  newStatus: variables.status,
                  totalAmount: invoice.total || 0,
                  balance: invoice.balance_due || invoice.balance || 0,
                  dueDate: invoice.due_date || '',
                  issueDate: invoice.issue_date || '',
                  shipments: shipmentsForEmail,
                  tripName: invoice.trip_name || ''
                });
              }
            }
          }
        } catch (emailError) {
          console.error('Failed to send invoice email:', emailError);
          // Don't show error to user - email is optional
        }
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: invoicesAPI.delete,
    onSuccess: async () => {
      // Refetch queries to ensure UI updates with correct data
      await queryClient.refetchQueries({ queryKey: ['invoices'] });
      toast({ title: 'Success', description: 'Invoice deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  return {
    invoices: data || [],
    isLoading,
    error,
    createInvoice: createMutation.mutate,
    updateInvoice: updateMutation.mutate,
    deleteInvoice: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
